import { Dispatch, RefObject, SetStateAction, useEffect, useImperativeHandle, useState } from 'react';

import { Grid } from '@mui/material';

import { TagPecaMarketingAutoComplete, TagPecaTradeAutoComplete } from 'components/autocompletes';
import { CissTextField, MarketingFormatoPecaField, StatusField } from 'components/fields';
import { Filter } from 'components/filter';
import { usePermissionsContext } from 'context/PermissionsContext';
import { FilterValuesProps, useFilter } from 'hooks/useFilter';
import { getTypeMarketing } from 'pages/marketing-trade/types';

export type RefMarketingTradeListFilter = {
    setFilterCampanha: Dispatch<SetStateAction<FilterValuesProps>>;
    filterCampanha: FilterValuesProps;
};

type MarketingTradeListFilter = {
    isMarketing?: boolean;
    ref?: RefObject<RefMarketingTradeListFilter | null>;
};

export function MarketingTradeListFilter({ ref, isMarketing = false }: MarketingTradeListFilter) {
    const { handleFilterSubmit } = useFilter();
    const { hasPermission } = usePermissionsContext();

    const [filterTitulo, setFilterTitulo] = useState<FilterValuesProps>({
        property: 'dsPecaMarketing',
        operator: 'like',
        value: '',
    });

    const [filterFormato, setFilterFormato] = useState<FilterValuesProps>({
        property: 'idPecaMarketingFormato',
        operator: 'eq',
        value: null,
        idProperty: 'idPecaMarketingFormato',
    });

    const [filterTags, setFilterTags] = useState<FilterValuesProps>({
        property: 'idTagsPecaTrade',
        operator: 'in',
        value: [],
        idProperty: 'idTagPecaTrade',
    });

    const [filterStatus, setFilterStatus] = useState<FilterValuesProps>({
        property: 'fgAtivo',
        operator: 'eq',
        value: hasPermission([isMarketing ? 'MARKETING_PECA_MODERAR' : 'TRADE_PECA_MODERAR']) ? null : true,
        idProperty: 'vlStatus',
    });

    const [filterCampanha, setFilterCampanha] = useState<FilterValuesProps>({
        property: 'idCampanhaMarketing',
        operator: 'in',
        value: [],
    });

    const filterTipoMarketing: FilterValuesProps = {
        property: 'idTipoMarketing',
        operator: 'eq',
        value: getTypeMarketing(isMarketing),
    };

    const filters = [
        { value: filterTitulo, setter: setFilterTitulo },
        { value: filterFormato, setter: setFilterFormato },
        { value: filterTags, setter: setFilterTags },
        { value: filterStatus, setter: setFilterStatus },
        { value: filterCampanha, setter: setFilterCampanha },
        { value: filterTipoMarketing },
    ];

    useImperativeHandle(ref, () => ({ setFilterCampanha, filterCampanha }), [filterCampanha]);

    useEffect(() => handleFilterSubmit({ filters }), [filterCampanha]);

    return (
        <Filter filters={filters}>
            <Grid sx={{ flex: 1 }}>
                <CissTextField
                    label="Nome ou título da peça"
                    value={filterTitulo.value}
                    onChange={(e) => setFilterTitulo((oldState: FilterValuesProps) => ({ ...oldState, value: e.target.value }))}
                />
            </Grid>
            <Grid sx={{ flex: 1 }}>
                <MarketingFormatoPecaField
                    value={filterFormato.value}
                    onChange={(e, value) => setFilterFormato((oldState: FilterValuesProps) => ({ ...oldState, value }))}
                />
            </Grid>
            <Grid sx={{ flex: 1 }}>
                {isMarketing ? (
                    <TagPecaMarketingAutoComplete
                        value={filterTags.value}
                        onChange={(e: any, value: any) => setFilterTags((oldState: FilterValuesProps) => ({ ...oldState, value }))}
                    />
                ) : (
                    <TagPecaTradeAutoComplete
                        value={filterTags.value}
                        onChange={(e: any, value: any) => setFilterTags((oldState: FilterValuesProps) => ({ ...oldState, value }))}
                    />
                )}
            </Grid>
            {hasPermission([isMarketing ? 'MARKETING_PECA_MODERAR' : 'TRADE_PECA_MODERAR']) && (
                <Grid sx={{ flex: 1 }}>
                    <StatusField
                        value={filterStatus.value}
                        onChange={(e, value) => setFilterStatus((oldState: FilterValuesProps) => ({ ...oldState, value }))}
                    />
                </Grid>
            )}
        </Filter>
    );
}
